/**
 * FormField component for rendering individual form fields
 */

import React, { memo } from 'react';
import { FormField as FormFieldType } from '@vulform/core';

interface FormFieldProps {
  field: FormFieldType;
  value: any;
  error?: string;
  disabled?: boolean;
  onChange: (value: any) => void;
  onBlur?: () => void;
  className?: string;
}

export const FormField = memo<FormFieldProps>(
  ({ field, value, error, disabled = false, onChange, onBlur, className = '' }) => {
    const fieldId = `vulform-${field.name}`;
    const isRequired = field.validation?.required;

    const baseInputClasses = [
      'vulform-input',
      'w-full px-4 py-3 border rounded-lg transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      error ? 'border-red-500' : 'border-gray-300',
      field.className || '',
    ]
      .filter(Boolean)
      .join(' ');

    const renderInput = () => {
      switch (field.type) {
        case 'textarea':
          return (
            <textarea
              id={fieldId}
              name={field.name}
              value={value || ''}
              onChange={e => onChange(e.target.value)}
              onBlur={onBlur}
              placeholder={field.placeholder}
              disabled={disabled}
              required={isRequired}
              rows={field.rows || 3}
              className={baseInputClasses}
            />
          );

        case 'select':
          return (
            <select
              id={fieldId}
              name={field.name}
              value={value || ''}
              onChange={e => onChange(e.target.value)}
              onBlur={onBlur}
              disabled={disabled}
              required={isRequired}
              className={baseInputClasses}
            >
              {field.placeholder && (
                <option value='' disabled>
                  {field.placeholder}
                </option>
              )}
              {field.options?.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          );

        default:
          return (
            <input
              type={field.type}
              id={fieldId}
              name={field.name}
              value={value || ''}
              onChange={e => onChange(e.target.value)}
              onBlur={onBlur}
              placeholder={field.placeholder}
              disabled={disabled}
              required={isRequired}
              className={baseInputClasses}
              step={field.step}
              min={field.validation?.min}
              max={field.validation?.max}
              minLength={field.validation?.minLength}
              maxLength={field.validation?.maxLength}
            />
          );
      }
    };

    return (
      <div className={`vulform-field-wrapper ${className}`.trim()}>
        <label
          htmlFor={fieldId}
          className='vulform-label block text-sm font-medium text-gray-700 mb-2'
        >
          {field.label}
          {isRequired && <span className='text-red-500'> *</span>}
        </label>

        {field.description && (
          <p className='vulform-description text-sm text-gray-600 mb-2'>{field.description}</p>
        )}

        {renderInput()}

        {error && <p className='vulform-error text-sm text-red-600 mt-1'>{error}</p>}
      </div>
    );
  }
);

FormField.displayName = 'FormField';

export default FormField;
